import { isAnyOf } from "@reduxjs/toolkit";

import type { AppStartListening } from "types/AppStartListening";

import { caseViewerActions, caseViewerThunks } from "./slice";
import { messagesActions } from "features/messages/store/slice";
import { DossierClientMethodNames } from "features/messages/hubs/sections/DossierSection";
import { PersistedRecordingStatusChanged } from "../../types/PersistedRecordingStatusChanged";
import { MessageArgsToType } from "features/messages/helpers";
import { DossierDeviceStatusChanged } from "features/cases/types/DossierDeviceStatusChanged";
import { PersistedRecordingPersistedStartAndStopTimeChanged } from "features/cases/types/PersistedRecordingPersistedStartAndStopTimeChanged";
import { DossierInformationChanged } from "features/cases/types/DossierInformationChanged";
import { reIdThunks } from "../reId/store/slice";

// listeners
export const addCaseViewerListeners = (startListening: AppStartListening) => {
    // when visibility of one or more dossier devices is changed we should update persisted recording state
    startListening({
        matcher: isAnyOf(
            caseViewerActions.upsertVisibleDossierDeviceIdState,
            caseViewerActions.setVisibilityAllDossierDevices,
        ),
        effect: async (action, listenerApi) => {
            listenerApi.dispatch(
                caseViewerThunks.removePersistedRecordingsAndStateForInvisibleDossierDevicesAsync(
                    undefined,
                ),
            );

            // close re-id view
            listenerApi.dispatch(reIdThunks.reIdViewStopAsync());
        },
    });

    // Listen for updates to persisted recordings of the active dossier
    startListening({
        predicate: (action) => {
            return (
                messagesActions.messageReceived.match(action) &&
                action.payload.messageType ===
                    DossierClientMethodNames.PersistedRecordingDownloadTaskStatusChanged
            );
        },
        effect: async (action, listenerApi) => {
            if (messagesActions.messageReceived.match(action)) {
                const persistedRecordingStatusChanged = MessageArgsToType<
                    PersistedRecordingStatusChanged[]
                >(action.payload.args);

                listenerApi.dispatch(
                    caseViewerActions.setPersistedRecordingsDownloadStatus(
                        persistedRecordingStatusChanged,
                    ),
                );
            }
        },
    });

    startListening({
        predicate: (action) => {
            return (
                messagesActions.messageReceived.match(action) &&
                action.payload.messageType ===
                    DossierClientMethodNames.DossierDeviceStatusChanged
            );
        },
        effect: async (action, listenerApi) => {
            if (messagesActions.messageReceived.match(action)) {
                const dossierDeviceStatusChanged =
                    MessageArgsToType<DossierDeviceStatusChanged>(
                        action.payload.args,
                    );

                listenerApi.dispatch(
                    caseViewerActions.setDossierDeviceStatus(
                        dossierDeviceStatusChanged,
                    ),
                );
            }
        },
    });

    startListening({
        predicate: (action) => {
            return (
                messagesActions.messageReceived.match(action) &&
                action.payload.messageType ===
                    DossierClientMethodNames.PersistedRecordingPersistedStartAndStopTimeChanged
            );
        },
        effect: async (action, listenerApi) => {
            if (messagesActions.messageReceived.match(action)) {
                const persistedRecordingPersistedStartAndStopTimeChanged =
                    MessageArgsToType<PersistedRecordingPersistedStartAndStopTimeChanged>(
                        action.payload.args,
                    );

                listenerApi.dispatch(
                    caseViewerActions.setPersistedRecordingPersistedStartAndStopTime(
                        persistedRecordingPersistedStartAndStopTimeChanged,
                    ),
                );
            }
        },
    });

    startListening({
        predicate: (action) => {
            return (
                messagesActions.messageReceived.match(action) &&
                action.payload.messageType ===
                    DossierClientMethodNames.DossierInformationChanged
            );
        },
        effect: async (action, listenerApi) => {
            if (messagesActions.messageReceived.match(action)) {
                const dossierInformationChanged =
                    MessageArgsToType<DossierInformationChanged>(
                        action.payload.args,
                    );

                listenerApi.dispatch(
                    caseViewerActions.setDossierInformation(
                        dossierInformationChanged,
                    ),
                );
            }
        },
    });
};
